import { type JSX, useMemo } from 'react';

import { Button, Modal, ModalBody, ModalFooter, ModalHeader, Text } from '@change/ds-core-ui';

import { Translate, useI18n } from '@change-corgi/core/react/i18n';

import { useVanityModalState } from 'src/app/pages/starterDashboardV2/shared/hooks';

import { ConfirmVanityLinkForm } from './components/ConfirmVanityLinkForm';
import { CreateVanityLinkForm } from './components/CreateVanityLinkForm';
import { CreateVanityLinkSuccess } from './components/CreateVanityLinkSuccess';

export function VanityLinkModal({
	onClose,
	trigger,
	petitionId,
	displayedSignatures,
	petitionSlug,
}: {
	onClose: () => void;
	trigger: JSX.Element;
	petitionId: string;
	displayedSignatures: number;
	petitionSlug: string;
}): JSX.Element {
	const { translate } = useI18n();

	const [{ viewState }] = useVanityModalState();
	const { modalTitle, modalDescription, modalFooter } = useMemo(() => {
		switch (viewState) {
			case 'CREATE':
				return {
					modalTitle: translate('corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_title'),
					modalDescription: translate(
						'corgi.petition.starter_dashboard_v2.grow_petition.copy_link.vanity_link_description',
					),
					modalFooter: undefined,
				};
			case 'CONFIRM':
				return {
					modalTitle: translate('corgi.petition.starter_dashboard_v2.grow_petition.copy_link.confirm_link_title'),
					modalDescription: (
						<div className="flex flex-col">
							<Text className="font-bold text-typography-weak">
								<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.confirm_link_description.first_line" />
							</Text>
							<Text className="text-typography-weak">
								<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.confirm_link_description.second_line" />
							</Text>
						</div>
					),
					modalFooter: undefined,
				};
			case 'SUCCESS':
			default:
				return {
					modalTitle: translate('corgi.petition.starter_dashboard_v2.grow_petition.copy_link.success_link_title'),
					modalDescription: undefined,
					modalFooter: (
						<ModalFooter>
							<Button data-qa="close-modal-button" size="small" variant="primary" onClick={onClose}>
								<Translate value="corgi.petition.starter_dashboard_v2.grow_petition.copy_link.close" />
							</Button>
						</ModalFooter>
					),
				};
		}
	}, [viewState, translate, onClose]);

	const modalBodyContent = useMemo(() => {
		switch (viewState) {
			case 'CREATE':
				return (
					<CreateVanityLinkForm
						data-qa="create-vanity-link-form"
						petitionId={petitionId}
						displayedSignatures={displayedSignatures}
					/>
				);
			case 'CONFIRM':
				return (
					<ConfirmVanityLinkForm
						data-qa="confirm-vanity-link-form"
						petitionSlug={petitionSlug}
						petitionId={petitionId}
						displayedSignatures={displayedSignatures}
					/>
				);
			case 'SUCCESS':
			default:
				return <CreateVanityLinkSuccess data-qa="create-vanity-link-success" />;
		}
	}, [viewState, petitionId, displayedSignatures, petitionSlug]);
	return (
		<Modal className="z-30" trigger={trigger} isDismissable onClose={onClose} data-testid="vanity-link-modal">
			<ModalHeader title={modalTitle} description={modalDescription} />
			<ModalBody>{modalBodyContent}</ModalBody>
			{modalFooter}
		</Modal>
	);
}
